import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Briefcase,
  ChevronDown,
  CreditCard,
  Info,
  Plus,
  User,
} from 'lucide-react';
import { useAuth } from '~/auth/use-auth';
import { m } from '~/paraglide/messages.js';
import cop from '~/static/icons/cop.png';
import { useAccounts } from '../../../-hooks/use-accounts';
import type { AccountsBancolombiaResponse } from '../../../accounts/-types/account';
import type { CardMetadata } from '../../-types/card';

interface DetailsTabProps {
  metadata: CardMetadata;
  cardUrn: string;
}

export function DetailsTab({ metadata, cardUrn }: DetailsTabProps) {
  return (
    <div className="space-y-6">
      <LinkedAccountsCard cardUrn={cardUrn} />
      <CardMetadataSection metadata={metadata} />
    </div>
  );
}

function LinkedAccountsCard({ cardUrn }: { cardUrn: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: accountsData, isLoading } =
    useAccounts<AccountsBancolombiaResponse>({
      holder_urn: user?.urn ?? '',
      medium: 'bancolombia',
    });

  const linkedAccounts = cardUrn
    ? (accountsData?.data?.accounts?.filter(
        (account) => account.metadata?.card_urn === cardUrn,
      ) ?? [])
    : (accountsData?.data?.accounts ?? []);

  return (
    <Card className="border-purple-500/30 bg-transparent shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {m['card.account_balance_summary.linked_accounts_title']()}
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate({ to: '/accounts' })}
          >
            {m['card.account_balance_summary.linked_accounts_view_all']()}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </div>
        ) : linkedAccounts.length === 0 ? (
          <div className="flex flex-col items-center py-6">
            <CreditCard className="mb-3 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">
              {m['card.account_balance_summary.linked_accounts_empty_title']()}
            </p>
            <p className="mb-4 text-xs text-muted-foreground text-center">
              {m[
                'card.account_balance_summary.linked_accounts_empty_description'
              ]()}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate({ to: '/accounts' })}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {m['card.account_balance_summary.linked_accounts_add_button']()}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {linkedAccounts.map((account) => {
              const lastFour = account.details.reference_code
                ? account.details.reference_code.slice(-4)
                : '0000';

              return (
                <button
                  type="button"
                  key={account.id}
                  className="flex w-full items-center gap-3 rounded-lg border border-purple-500/20 p-3 text-left transition-colors hover:border-purple-500"
                  onClick={() => navigate({ to: '/accounts' })}
                >
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    {account.metadata?.name && (
                      <span className="text-sm font-medium truncate">
                        {account.metadata.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono">
                      ••{lastFour}
                    </span>
                    <div className="flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs">
                      <img src={cop} alt="COP" className="h-3.5 w-3.5" />
                      <span>
                        {m['card.account_balance_summary.asset_pesos']()}
                      </span>
                      <ChevronDown className="h-3 w-3" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CardMetadataSection({ metadata }: { metadata: CardMetadata }) {
  const hasMetadata =
    metadata.assigned_to || metadata.department || metadata.purpose;

  return (
    <Card className="border-purple-500/30 bg-transparent shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {m['cards.detail.card_info_title']()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasMetadata ? (
          <p className="text-sm text-muted-foreground">
            {m['cards.detail.no_metadata']()}
          </p>
        ) : (
          <div className="space-y-3">
            {metadata.assigned_to && (
              <div className="flex items-center gap-3 rounded-lg border border-purple-500/20 p-3">
                <User className="h-4 w-4 text-purple-400" />
                <div>
                  <p className="text-[10px] text-muted-foreground">
                    {m['cards.detail.assigned_to']()}
                  </p>
                  <p className="text-sm font-medium">{metadata.assigned_to}</p>
                </div>
              </div>
            )}
            {metadata.department && (
              <div className="flex items-center gap-3 rounded-lg border border-purple-500/20 p-3">
                <Briefcase className="h-4 w-4 text-purple-400" />
                <div>
                  <p className="text-[10px] text-muted-foreground">
                    {m['cards.detail.department']()}
                  </p>
                  <p className="text-sm font-medium">{metadata.department}</p>
                </div>
              </div>
            )}
            {metadata.purpose && (
              <div className="flex items-center gap-3 rounded-lg border border-purple-500/20 p-3">
                <Info className="h-4 w-4 text-purple-400" />
                <div>
                  <p className="text-[10px] text-muted-foreground">
                    {m['cards.detail.purpose']()}
                  </p>
                  <p className="text-sm font-medium">{metadata.purpose}</p>
                </div>
              </div>
            )}
            {metadata.template && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {m['cards.spending.template']()}:
                </span>
                <Badge variant="secondary" className="capitalize text-xs">
                  {metadata.template}
                </Badge>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
